
import { Component, input, signal, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import {
  ActionBarRight,
  AwActionBarComponent,
  AwDialogComponent,
  AwDividerComponent,
  AwSpinnerComponent,
  AwToastComponent,
  BreadCrumb,
  DialogFormControls,
  DialogOptions,
  ToastVariants,
  SingleSelectOption,
  AwBreadCrumbComponent,
} from '@assetworks-llc/aw-component-lib';

import { AwTextFormComponent } from '../../../../../shared/components/aw-text-form/aw-text-form.component';
import { AwDropdownFormComponent } from '../../../../../shared/components/aw-dropdown-form/aw-dropdown-form.component';
import { Router } from '@angular/router';

import {
  catchError,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { AwAuthFormComponent } from '../../../components/aw-auth-form/aw-auth-form.component';
import {
  AuthInputValues,
  AuthTypeValues,
  DEFAULT_AUTH_VALUES,
} from '../../../../../types/types';
import { IntegrationService } from '../../../../../core/services/integration.service';
import { AuthenticationService } from '../../../../../core/services/authentication.service';
import {
  CreateIntegrationConfigurationRequest,
  CreateSecretRequest,
} from '../../../../../api/models';
import { SecretService } from '../../../../../core/services/secret.service';
import { buildAuthSecretName } from '../../../../../shared/utils/auth.utils';
import { buildClientSecretName } from '../../../../../shared/utils/secrets.utils';
import { EmployeeAuthenticationService } from '../../../../../core/services/employee-authentication.service';

import { isValidOrganizationName, ORGANIZATION_NAME_MAX_LENGTH, ORGANIZATION_NAME_PATTERN, ORGANIZATION_NAME_PATTERN_MESSAGE } from '../../../../../shared/utils/organization-name';

import { UnsavedChangesForm } from '../../../../../shared/unsaved-changes/unsaved-changes-form';
import { UnsavedChangesDialogComponent } from '../../../../../shared/unsaved-changes/unsaved-changes-dialog.component';

@Component({
    selector: 'app-add-integration',
    imports: [
    AwDividerComponent,
    AwBreadCrumbComponent,
    AwTextFormComponent,
    AwSpinnerComponent,
    AwActionBarComponent,
    AwDialogComponent,
    AwDropdownFormComponent,
    AwToastComponent,
    AwAuthFormComponent,
    UnsavedChangesDialogComponent,
],
    templateUrl: './add-integration.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        './add-integration.component.scss',
        '../../../../../../styles/management.scss',
    ]
})
export class AddIntegrationComponent extends UnsavedChangesForm {
  private readonly router = inject(Router);
  private readonly integrationService = inject(IntegrationService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly employeeAuthenticationService = inject(EmployeeAuthenticationService);
  private readonly secretService = inject(SecretService);

  readonly nameMaxLength = ORGANIZATION_NAME_MAX_LENGTH;
  readonly namePattern = ORGANIZATION_NAME_PATTERN;
  readonly namePatternMessage = ORGANIZATION_NAME_PATTERN_MESSAGE;

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;

  isLoading: boolean = false;

  selectedDate: Date | null = null;
  integrationNameInput: string = '';
  integrationStatusInput: string = '';
  integrationStatusValue!: boolean;
  apiAuthTypeInput: string = '';
  apiAuthTypeValue!: AuthTypeValues;

  employeeAuthTypeInput: string = '';
  employeeAuthTypeValue!: AuthTypeValues;

  apiAuthFieldsValues: AuthInputValues = DEFAULT_AUTH_VALUES;
  apiAuthFieldsFilled: boolean = false;
  apiAuthAttempted: boolean = false;

  employeeAuthFieldsValues: AuthInputValues = DEFAULT_AUTH_VALUES;
  employeeAuthFieldsFilled: boolean = false;
  employeeAuthAttempted: boolean = false;

  get apiAuthValues(): AuthInputValues {
    return this.apiAuthFieldsValues;
  }

  get employeeAuthValues(): AuthInputValues {
    return this.employeeAuthFieldsValues;
  }

  openIntegrationInfoDialog: boolean = false;

  generatedSecret: string = '';
  generatedClientId: string = '';

  breadcrumbs = signal<BreadCrumb[]>([
    {
      label: 'Third-Party Integration Management',
      route: '/integration-management/overview',
      newTab: false,
    },
    {
      label: 'New Third-Party Integration',
      route: '/integration-management/add-integration',
      newTab: false,
    },
  ]);

  statusOptions = signal<SingleSelectOption[]>([
    {
      label: 'Active',
    },
    {
      label: 'Disabled',
    },
  ]);

  apiAuthTypeOptions = signal<SingleSelectOption[]>([
    {
      label: 'OAuth2',
    },
  ]);

  employeeAuthTypeOptions = signal<SingleSelectOption[]>([
    {
      label: 'OpenID Connect',
    },
  ]);

  dialogOptions: DialogOptions = {
    variant: 'alert',
    title: 'Third-Party Integration Info',
    description:
      'Keep track of this information. It will not be provided again after this.',
    primaryButtonLabel: 'Close',
  };

  formFieldsData: DialogFormControls = {
    controls: [],
  };

  actionsRight = input<ActionBarRight[]>([
    {
      buttonCallback: {
        label: 'Cancel',
        buttonType: 'primary',
        action: () => this.router.navigate(['/integration-management/overview']),
      },
    },
    {
      buttonCallback: {
        label: 'Save',
        buttonType: 'outlined',
        action: () => this.createIntegration(),
      },
    },
  ]);

  onDateSelected(date: Date | null) {
    this.selectedDate = date;
  }

  onIntegrationNameSelected(text: string | null) {
    this.integrationNameInput = text || '';
  }

  onIntegrationStatusSelected(status: SingleSelectOption | null) {
    this.integrationStatusInput = status?.label || '';

    if (status?.label === 'Active') {
      this.integrationStatusValue = true;
    } else if (status?.label === 'Disabled') {
      this.integrationStatusValue = false;
    }
  }

  onIntegrationAuthTypeSelected(authType: SingleSelectOption | null) {
    this.apiAuthTypeInput = authType?.label || '';

    if (authType?.label === 'OAuth2') {
      this.apiAuthTypeValue = AuthTypeValues.OAUTH2;
      this.apiAuthFieldsValues = DEFAULT_AUTH_VALUES;
    } else if (authType?.label === 'EntraID') {
      this.apiAuthTypeValue = AuthTypeValues.ENTRAID;
      this.apiAuthFieldsValues = DEFAULT_AUTH_VALUES;
    } else if (authType?.label === 'OpenID Connect') {
      this.apiAuthTypeValue = AuthTypeValues.OPENIDCONNECT;
      this.apiAuthFieldsValues = DEFAULT_AUTH_VALUES;
    }
  }
  onEmployeeAuthTypeSelected(authType: SingleSelectOption | null) {
    this.employeeAuthTypeInput = authType?.label || '';

    if (authType?.label === 'OAuth2') {
      this.employeeAuthTypeValue = AuthTypeValues.OAUTH2;
      this.employeeAuthFieldsValues = DEFAULT_AUTH_VALUES;
    } else if (authType?.label === 'EntraID') {
      this.employeeAuthTypeValue = AuthTypeValues.ENTRAID;
      this.employeeAuthFieldsValues = DEFAULT_AUTH_VALUES;
    } else if (authType?.label === 'OpenID Connect') {
      this.employeeAuthTypeValue = AuthTypeValues.OPENIDCONNECT;
      this.employeeAuthFieldsValues = DEFAULT_AUTH_VALUES;
    }
  }

  onApiAuthValuesChanged(event: {
    values: AuthInputValues;
    fieldsFilled: boolean;
    someFieldsFilled: boolean;
  }) {
    this.apiAuthFieldsFilled = event.fieldsFilled;
    this.apiAuthFieldsValues = event.values;
    this.apiAuthAttempted = event.someFieldsFilled;
  }

  onEmployeeAuthValuesChanged(event: {
    values: AuthInputValues;
    fieldsFilled: boolean;
    someFieldsFilled: boolean;
  }) {
    this.employeeAuthFieldsFilled = event.fieldsFilled;
    this.employeeAuthFieldsValues = event.values;
    this.employeeAuthAttempted = event.someFieldsFilled;
  }

  dialogCloseHandler() {
    this.openIntegrationInfoDialog = false;
    this.router.navigate(['/integration-management/overview']).then(() => {
      window.location.reload();
    });
  }

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  validateIntegrationFields(): boolean {
    return (
      [
        AuthTypeValues.OAUTH2,
        AuthTypeValues.ENTRAID,
        AuthTypeValues.OPENIDCONNECT,
      ].includes(this.apiAuthTypeValue as number) &&
      this.apiAuthTypeInput !== '' &&
      isValidOrganizationName(this.integrationNameInput) &&
      this.integrationStatusValue !== null &&
      this.integrationStatusInput !== ''
    );
  }

  createIntegration(): void {
    if (!this.validateIntegrationFields()) {
      this.buildToastMessage(
        'Fill in all required third-party integration fields.',
        'error'
      );
      return;
    }

    if (this.apiAuthAttempted && !this.apiAuthFieldsFilled) {
      this.buildToastMessage(
        'Fill in all required API authentication fields',
        'error'
      );
      return;
    }

    if (this.employeeAuthAttempted && !this.employeeAuthFieldsFilled) {
      this.buildToastMessage(
        'Fill in all required third-party employee authentication fields',
        'error'
      );
      return;
    }

    console.log(this.apiAuthFieldsValues, '/n', this.employeeAuthFieldsValues);

    this.isLoading = true;

    const integrationSecretName: string = buildClientSecretName();
    const addIntegrationBody: CreateIntegrationConfigurationRequest = {
      authenticationType: this.apiAuthTypeValue,
      clientSecretName: integrationSecretName,
      enabled: this.integrationStatusValue,
      organizationName: this.integrationNameInput,
    };

    this.integrationService
      .createIntegration({ body: addIntegrationBody })
      .pipe(
        switchMap((newIntegration) => {
          this.generatedClientId = newIntegration.clientId || '';
          this.markSaved();
          this.generatedSecret = newIntegration.clientSecret || '';
          this.formFieldsData = {
            controls: [
              {
                formControlName: 'secret_id',
                label: 'Client ID',
                value: this.generatedClientId,
                type: 'text',
                readOnly: true,
                fieldActions: [
                  {
                    iconButtonCallback: {
                      action: () => this.copyToClipboard('' + newIntegration.clientId),
                      icon: "copy",
                      label: "Click to copy the client id to the clipboard",
                    }
                  }
                ],
              },
              {
                formControlName: 'secret_name',
                label: 'Client Secret',
                value: this.generatedSecret,
                type: 'text',
                readOnly: true,
                fieldActions: [
                  {
                    iconButtonCallback: {
                      action: () => this.copyToClipboard('' +  newIntegration.clientSecret),
                      icon: "copy",
                      label: "Click to copy the client secret to the clipboard",
                    }
                  }
                ],
              },
            ],
          };
          this.openIntegrationInfoDialog = true;
          if (!this.apiAuthFieldsFilled && !this.employeeAuthFieldsFilled) {
            return of({
              integrationSuccess: true,
              authResult: false,
              authAttempted: false,
            });
          }

          // User attempted to add authentication, Begin auth creation flow
          const apiAuthSecretName: string = buildAuthSecretName(
            this.apiAuthTypeValue, 'api'
          );
          const employeeAuthSecretName: string = buildAuthSecretName(
            this.employeeAuthTypeValue, 'employee'
          );

          const apiSecretInfo: CreateSecretRequest = {
            secretName: apiAuthSecretName,
            secretValue:
              this.apiAuthFieldsValues.client_secret ??
              `${apiAuthSecretName}Value`,
          };
          const employeeSecretInfo: CreateSecretRequest = {
            secretName: employeeAuthSecretName,
            secretValue:
              this.employeeAuthFieldsValues.client_secret ??
              `${employeeAuthSecretName}Value`,
          };

          const requests: { [key: string]: Observable<any> } = {};
          if (this.apiAuthFieldsFilled) {
            requests['apiAuthCreation'] = this.secretService
              .createSecret({
                organizationId: this.generatedClientId,
                body: apiSecretInfo,
              })
              .pipe(
                switchMap(() =>
                  this.authenticationService.createAuthenticationConfig({
                    organizationId: this.generatedClientId,
                    authType: this.apiAuthTypeValue,
                    authConfigValues: this.apiAuthFieldsValues,
                    secretName: apiAuthSecretName,
                  })
                ),
                map(() => ({ attempted: true, success: true })),
                catchError(() => of({ attempted: true, success: false }))
              );
          } else {
            requests['apiAuthCreation'] = of({
              attempted: false,
              success: false,
            });
          }
          if (this.employeeAuthFieldsFilled) {
            requests['employeeAuthCreation'] = this.secretService
              .createSecret({
                organizationId: this.generatedClientId,
                body: employeeSecretInfo,
              })
              .pipe(
                switchMap(() =>
                  this.employeeAuthenticationService.createAuthenticationConfig(
                    {
                      organizationId: this.generatedClientId,
                      authType: this.employeeAuthTypeValue,
                      authConfigValues: this.employeeAuthFieldsValues,
                      secretName: employeeAuthSecretName,
                    }
                  )
                ),
                map(() => ({ attempted: true, success: true })),
                catchError(() => of({ attempted: true, success: false }))
              );
          } else {
            requests['employeeAuthCreation'] = of({
              attempted: false,
              success: false,
            });
          }

          return forkJoin(requests).pipe(
            map(({ apiAuthCreation, employeeAuthCreation }) => {
              const authAttempted: boolean =
                apiAuthCreation.attempted || employeeAuthCreation.attempted;
              const apiAuthResult = apiAuthCreation.attempted
                ? apiAuthCreation.success
                : true;
              const employeeAuthResult = employeeAuthCreation.attempted
                ? employeeAuthCreation.success
                : true;
              const authResult = apiAuthResult && employeeAuthResult;

              return {
                integrationSuccess: true,
                authResult: authResult,
                authAttempted: authAttempted,
              };
            })
          );
        }),
        catchError((error) => {
          this.isLoading = false;
          this.buildToastMessage(
            'Failed to create third-party integration. Please try again',
            'error'
          );
          return of({
            integrationSuccess: false,
            authResult: false,
            authAttempted: false,
          });
        }),
        finalize(() => {
          this.generatedClientId = '';
          this.generatedSecret = '';
          this.isLoading = false;
        })
      )
      .subscribe(({ integrationSuccess, authResult, authAttempted }) => {
        if (!integrationSuccess) {
          return;
        }

        if (authAttempted) {
          this.buildToastMessage(
            authResult
              ? 'Successfully created third-party integration and authentication'
              : 'Failed to create authentication',
            authResult ? 'success' : 'error'
          );
        } else {
          this.buildToastMessage(
            'Successfully created third-party integration.',
            'success'
          );
        }
      });
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.buildToastMessage('Copied to clipboard', 'success');
    });
  }

  protected formState(): unknown {
    return {
      name: this.integrationNameInput,
      status: this.integrationStatusInput,
      apiAuthType: this.apiAuthTypeInput,
      employeeAuthType: this.employeeAuthTypeInput,
      apiAuth: this.apiAuthAttempted ? this.apiAuthFieldsValues : null,
      employeeAuth: this.employeeAuthAttempted ? this.employeeAuthFieldsValues : null,
    };
  }
}
